/* START_MODULE_CONTRACT
 * PURPOSE: Расчёт overhead на дату и net-ёмкости роли для timeline (UC-014).
 * SCOPE: isOverheadActiveOnDate, sumOverheadHoursForRole, computeNetDayCapacity.
 * DEPENDS: overheadConstants, dateUtils
 * LINKS: M-FE-OVERHEAD-SCHEDULE, M-OVERHEAD-DAY-MATCH, V-M-FE-OVERHEAD-SCHEDULE
 * ROLE: RUNTIME
 * MAP_MODE: EXPORTS
 * END_MODULE_CONTRACT
 *
 * START_MODULE_MAP
 * isOverheadActiveOnDate - recurrence match on calendar date
 * sumOverheadHoursForRole - sum hoursPerEmployee for role on date
 * computeNetDayCapacity - gross workload capacity minus overhead
 * END_MODULE_MAP
 *
 * START_CHANGE_SUMMARY
 * LAST_CHANGE: 2026-07-03 - UC-014: parity with OverheadDayMatcher.
 * END_CHANGE_SUMMARY
 */

import { OVERHEAD_RECURRENCE_TYPES } from '../../models/overheadConstants';
import { toIsoDate } from '../../utils/dateUtils';

/* START_METHOD_toIsoDayOfWeek */
/* START_CONTRACT: toIsoDayOfWeek
 * PURPOSE: Преобразовать Date в ISO день недели (1=пн … 7=вс).
 * INPUTS: { date: Date }.
 * OUTPUTS: { number }.
 * SIDE_EFFECTS: нет.
 * LINKS: M-FE-OVERHEAD-SCHEDULE
 * END_CONTRACT: toIsoDayOfWeek
 */
function toIsoDayOfWeek(date) {
  const day = date.getDay();
  return day === 0 ? 7 : day;
}
/* END_METHOD_toIsoDayOfWeek */

/* START_METHOD_daysBetween */
/* START_CONTRACT: daysBetween
 * PURPOSE: Целое число календарных дней от anchor до target (anchor inclusive as 0).
 * INPUTS: { anchorIso: string, targetIso: string }.
 * OUTPUTS: { number }.
 * SIDE_EFFECTS: нет.
 * LINKS: M-FE-OVERHEAD-SCHEDULE
 * END_CONTRACT: daysBetween
 */
function daysBetween(anchorIso, targetIso) {
  const [ay, am, ad] = anchorIso.split('-').map(Number);
  const [ty, tm, td] = targetIso.split('-').map(Number);
  const anchorMs = Date.UTC(ay, am - 1, ad);
  const targetMs = Date.UTC(ty, tm - 1, td);
  return Math.round((targetMs - anchorMs) / (24 * 60 * 60 * 1000));
}
/* END_METHOD_daysBetween */

/* START_METHOD_resolveAnchorIso */
function resolveAnchorIso(overhead, fallbackIso) {
  if (overhead?.createdAt) {
    const d = overhead.createdAt instanceof Date
      ? overhead.createdAt
      : new Date(overhead.createdAt);
    return toIsoDate(d);
  }
  return fallbackIso;
}
/* END_METHOD_resolveAnchorIso */

/* START_METHOD_isOverheadActiveOnDate */
/* START_CONTRACT: isOverheadActiveOnDate
 * PURPOSE: Проверить, активен ли overhead на dateIso по правилу периодичности.
 * INPUTS: { overhead, dateIso: string }.
 * OUTPUTS: { boolean }.
 * SIDE_EFFECTS: нет.
 * LINKS: M-OVERHEAD-DAY-MATCH, V-M-FE-OVERHEAD-SCHEDULE
 * END_CONTRACT: isOverheadActiveOnDate
 */
export function isOverheadActiveOnDate(overhead, dateIso) {
  if (!overhead?.recurrenceType || !dateIso) {
    return false;
  }

  const anchorIso = resolveAnchorIso(overhead, dateIso);
  const { recurrenceType, intervalDays, daysOfWeek } = overhead;

  if (recurrenceType === OVERHEAD_RECURRENCE_TYPES.DAILY) {
    return true;
  }

  if (recurrenceType === OVERHEAD_RECURRENCE_TYPES.INTERVAL) {
    const n = intervalDays ?? 0;
    if (n < 1) {
      return false;
    }
    const delta = daysBetween(anchorIso, dateIso);
    return delta >= 0 && delta % n === 0;
  }

  if (recurrenceType === OVERHEAD_RECURRENCE_TYPES.WEEKLY) {
    if (!Array.isArray(daysOfWeek) || daysOfWeek.length === 0) {
      return false;
    }
    const [year, month, day] = dateIso.split('-').map(Number);
    const isoDay = toIsoDayOfWeek(new Date(year, month - 1, day));
    return daysOfWeek.includes(isoDay);
  }

  return false;
}
/* END_METHOD_isOverheadActiveOnDate */

/* START_METHOD_sumOverheadHoursForRole */
/* START_CONTRACT: sumOverheadHoursForRole
 * PURPOSE: Суммировать hoursPerEmployee активных overhead для роли на дату.
 * INPUTS: { overheads: array, roleId: number, dateIso: string }.
 * OUTPUTS: { number }.
 * SIDE_EFFECTS: нет.
 * LINKS: V-M-FE-OVERHEAD-SCHEDULE
 * END_CONTRACT: sumOverheadHoursForRole
 */
export function sumOverheadHoursForRole(overheads, roleId, dateIso) {
  if (!overheads?.length || roleId == null || !dateIso) {
    return 0;
  }

  return overheads.reduce((total, overhead) => {
    const roleIds = overhead.roleIds ?? [];
    if (!roleIds.includes(roleId)) {
      return total;
    }
    if (!isOverheadActiveOnDate(overhead, dateIso)) {
      return total;
    }
    const hours = Number(overhead.hoursPerEmployee);
    return total + (Number.isFinite(hours) ? hours : 0);
  }, 0);
}
/* END_METHOD_sumOverheadHoursForRole */

/* START_METHOD_computeNetDayCapacity */
/* START_CONTRACT: computeNetDayCapacity
 * PURPOSE: Net-ёмкость дня = gross (рабочие часы × workload%) − overhead, не ниже 0.
 * INPUTS: { grossDayHours, roleId, dateIso, overheads }.
 * OUTPUTS: { number }.
 * SIDE_EFFECTS: нет.
 * LINKS: M-FE-TIMELINE, V-M-FE-OVERHEAD-SCHEDULE
 * END_CONTRACT: computeNetDayCapacity
 */
export function computeNetDayCapacity(grossDayHours, roleId, dateIso, overheads) {
  const gross = Number(grossDayHours);
  if (!(gross > 0)) {
    return 0;
  }
  const overhead = sumOverheadHoursForRole(overheads, roleId, dateIso);
  return Math.max(0, gross - overhead);
}
/* END_METHOD_computeNetDayCapacity */
